import { PlusOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Divider,
  Drawer,
  Flex,
  Input,
  Popconfirm,
  Select,
  Space,
  Typography,
} from "antd";
import Title from "antd/es/typography/Title";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "../../../../api";
import Loader from "../../../../components/Loader";
import { IntTplLegalType } from "../../../../domain/types";
import AdminLangSwicher from "../../../admin_components/AdminLangSwicher";
import { useAppSelector } from "../../../../store";
import { AppState } from "../../../../store/appSlice";
import React from "react";
const Text = Typography;

const InternationalTplLegalTypes = () => {
  const { t } = useTranslation();
  const { field_Language } = useAppSelector(
    (data: { app: AppState }) => data.app,
  );
  const [drawerStatus, setDrawerStatus] = useState<boolean>(false);
  const [editedLegalType, setEditedLegalType] =
    useState<Partial<IntTplLegalType> | null>(null);
  console.log("editedLegalType", editedLegalType);

  const { data: clientTypes, isLoading: isClientTypesLoading } =
    api.useGetClientTypesQuery(undefined);

  const {
    data: IntTplClientTypesList,
    refetch: refetchIntTplClientTypes,
    isLoading: isIntTplClientTypesLoading,
  } = api.useGetIntTplClientTypesQuery(undefined);

  const {
    data: intTplClientTypeById,
    isLoading: isIntTplClientTypeByIdLoading,
  } = api.useGetIntTplClientTypeByIdQuery(editedLegalType?.id as number, {
    skip: !editedLegalType?.id,
  });

  const [createClientType, { isLoading: isCreateIntTplClientTypeLoading }] =
    api.useCreateIntTplClientTypeMutation();

  const [updateClientType, { isLoading: isUpdateIntTplClientTypeLoading }] =
    api.useUpdateIntTplClientTypeMutation();

  const onCloseDrawer = () => {
    setDrawerStatus(false);
  };

  const setEditedField = (field: keyof IntTplLegalType, value: any) => {
    setEditedLegalType({
      clientTypeId: clientTypes?.find((x) => x.slug === "legal")?.id,
      ...editedLegalType,
      [field]: value,
    });
  };

  const [
    deleteClientType,
    {
      isLoading: isDeleteIntTplClientTypeLoading,
      isSuccess: deleteIntTplClientTypeSuccess,
    },
  ] = api.useDeleteIntTplClientTypeMutation();

  const buildPayload = (data: Partial<IntTplLegalType>) => ({
    ...data,
    ligaAPIId: data.ligaApiId,
  });

  const _createClientType = async () => {
    if (editedLegalType) {
      try {
        await createClientType(
          buildPayload(editedLegalType) as Partial<IntTplLegalType>,
        ).unwrap();
        refetchIntTplClientTypes();
        toast.success(`${t("legalEntityType")} ${t("addedSuccessfully")}`);
        onCloseDrawer();
      } catch (error) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0];
          const errorMessage = firstError.message;
          toast.error(errorMessage);
        } else {
          toast.error(`${t("errorOccurred")}`);
        }
      }
    } else {
      toast.error(`${t("fillAllFields")}`);
    }
  };

  const _updateClientType = async () => {
    if (editedLegalType) {
      try {
        await updateClientType(
          buildPayload(editedLegalType) as Partial<IntTplLegalType>,
        ).unwrap();
        refetchIntTplClientTypes();
        toast.success(`${t("legalEntityType")} ${t("updatedSuccessfully")}`);
        onCloseDrawer();
      } catch (error) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0];
          const errorMessage = firstError.message;
          toast.error(errorMessage);
        } else {
          toast.error(`${t("errorOccurred")}`);
        }
      }
    } else {
      toast.error(`${t("fillAllFields")}`);
    }
  };

  useEffect(() => {
    if (deleteIntTplClientTypeSuccess) {
      refetchIntTplClientTypes();
    }
  }, [deleteIntTplClientTypeSuccess, refetchIntTplClientTypes]);

  useEffect(() => {
    if (intTplClientTypeById) {
      setEditedLegalType(intTplClientTypeById);
      setDrawerStatus(true);
    }

    return () => {};
  }, [intTplClientTypeById]);

  const MainLoader = isIntTplClientTypesLoading || isClientTypesLoading;

  const DrawerLoader =
    isUpdateIntTplClientTypeLoading ||
    isDeleteIntTplClientTypeLoading ||
    isIntTplClientTypeByIdLoading ||
    isCreateIntTplClientTypeLoading;

  return (
    <>
      {MainLoader ? (
        <Loader />
      ) : (
        <>
          <Flex justify="space-between">
            <Button
              size="large"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => [setEditedField("id", 0), setDrawerStatus(true)]}
            >
              {t("create")}
            </Button>

            <AdminLangSwicher />
          </Flex>

          <Divider />

          <Flex gap={20} wrap="wrap" justify="space-evenly">
            {IntTplClientTypesList?.map((item, index) => (
              <Badge.Ribbon
                key={index}
                style={{
                  marginTop: "-20px",
                }}
                text={item.status === "active" ? t("active") : t("inActive")}
                color={item.status === "active" ? "green" : "red"}
              >
                <Card
                  hoverable
                  style={{
                    maxWidth: "250px",
                    width: "100vw",
                    display: "grid",
                    placeItems: "center",
                    height: "100%",
                  }}
                  onClick={() => [
                    setEditedField("id", item.id),
                    setDrawerStatus(!drawerStatus),
                  ]}
                >
                  <Title level={5}>
                    {item.translations?.[field_Language]?.name ||
                      t("textNotFound")}
                  </Title>
                </Card>
              </Badge.Ribbon>
            ))}
          </Flex>

          <Drawer
            title={editedLegalType?.id === 0 ? t("create") : t("update")}
            onClose={onCloseDrawer}
            open={drawerStatus}
            extra={<AdminLangSwicher />}
          >
            {DrawerLoader ? (
              <Loader />
            ) : (
              <Flex vertical gap={20}>
                <Space direction="vertical">
                  <Text>{t("clientType")}</Text>
                  <Input
                    size="large"
                    placeholder={t("clientType")}
                    value={
                      clientTypes?.find((x) => x.slug === "legal")
                        ?.translations?.[field_Language]?.name || ""
                    }
                    disabled={true}
                  />
                </Space>

                <Space direction="vertical">
                  <Text>{t("legalEntityType")}</Text>
                  <Input
                    size="large"
                    placeholder={t("legalEntityType")}
                    value={
                      editedLegalType?.translations?.[field_Language]?.name ||
                      ""
                    }
                    onChange={(event) =>
                      setEditedField("translations", {
                        ...editedLegalType?.translations,
                        [field_Language]: {
                          ...editedLegalType?.translations?.[field_Language],
                          name: event?.target?.value,
                        },
                      })
                    }
                  />
                </Space>

                <Space direction="vertical">
                  <Text>{t("Liga API ID")}</Text>
                  <Input
                    size="large"
                    placeholder={t("ligaApiId")}
                    value={editedLegalType?.ligaApiId || ""}
                    onChange={(event) =>
                      setEditedField("ligaApiId", event?.target?.value)
                    }
                  />
                </Space>

                <Space direction="vertical">
                  <Text>{t("status")}</Text>
                  <Select
                    size="large"
                    showSearch
                    style={{ width: "100%", display: "block" }}
                    placeholder={t("status")}
                    optionFilterProp="label"
                    allowClear
                    value={editedLegalType?.status || undefined}
                    onChange={(value) => setEditedField("status", value)}
                    options={[
                      {
                        status: "active",
                        name: t("active"),
                      },
                      {
                        status: "inactive",
                        name: t("inActive"),
                      },
                    ]?.map((item) => ({
                      value: item.status,
                      label: item.name,
                    }))}
                  />
                </Space>

                <>
                  <Divider style={{ margin: 0 }} />
                  {editedLegalType?.id === 0 ? (
                    <Button
                      disabled={
                        editedLegalType && editedLegalType.status ? false : true
                      }
                      size="large"
                      type="primary"
                      onClick={_createClientType}
                    >
                      {t("create")}
                    </Button>
                  ) : null}

                  {editedLegalType &&
                  editedLegalType.id &&
                  editedLegalType.id !== 0 ? (
                    <>
                      <Button
                        size="large"
                        type="primary"
                        onClick={_updateClientType}
                      >
                        {t("update")}
                      </Button>

                      <Popconfirm
                        title={t("certainToDeleteIt")}
                        onConfirm={() => [
                          deleteClientType(editedLegalType?.id as number),
                          onCloseDrawer(),
                        ]}
                        onCancel={() => {}}
                        okText="Yes"
                        cancelText="No"
                        placement="bottom"
                      >
                        <Button
                          danger
                          size="large"
                          style={{
                            width: "100%",
                          }}
                        >
                          {t("delete")}
                        </Button>
                      </Popconfirm>
                    </>
                  ) : null}
                </>
              </Flex>
            )}
          </Drawer>
        </>
      )}
    </>
  );
};

export default InternationalTplLegalTypes;
